// SHER-406 guard helpers for the patient data-collection page (index.jsx).
//
// Route: /patient/:uuidPatient/:action/data-collection/:uuidDataCollection.
// The page resolves the selected survey with
// `surveys.find(sur => sur.uuid === uuidDataCollection)`. When the URL points
// at a survey that is no longer in the loaded surveys (deleted server-side, a
// stale/bookmarked link, or not yet loaded), `.find` returns `undefined`. The
// old render guard tested `dataCollectionSelected !== null`, which `undefined`
// passes, so the code went on to read `dataCollectionSelected.uuid` and crashed
// with "Cannot read properties of undefined (reading 'uuid')".
//
// These pure helpers capture the two decisions the component makes so they can
// be unit-tested without mounting the fully-connected page.

interface SurveyLike {
    uuid: string;
}

/**
 * Mirror of the page's survey resolution for the `uuidDataCollection` branch:
 * returns the matching survey, or `undefined` when the UUID is not among the
 * loaded surveys.
 */
export function resolveDataCollectionSelected(
    surveys: SurveyLike[] | null | undefined,
    uuidDataCollection: string | null | undefined
): SurveyLike | undefined {
    if (!surveys || !uuidDataCollection) return undefined;
    return surveys.find(sur => sur.uuid === uuidDataCollection);
}

/**
 * True when the URL asked for a specific survey (`uuidDataCollection` present)
 * but it could not be resolved — the page should render the Loader / wait
 * instead of dereferencing an undefined survey.
 */
export function shouldRenderLoaderForMissingSurvey(
    uuidDataCollection: string | null | undefined,
    dataCollectionSelected: SurveyLike | null | undefined
): boolean {
    return Boolean(uuidDataCollection) && !dataCollectionSelected;
}
